import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../modules/authentication/AuthProvider';
import { database } from '../lib/supabase';

type NewspaperRequest = {
    id: string;
    user_id: string;
    title: string | null;
    description: string | null;
    status: string;
    newspaper_date: string | null;
    is_customized: boolean;
    created_at: string;
    updated_at: string;
};

export function useNewspaperRequest() {
    const [newspaperRequest, setNewspaperRequest] = useState<NewspaperRequest | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        if (!currentUser) {
            setNewspaperRequest(null);
            return;
        }

        setIsLoading(true);

        const loadNewspaperRequest = async () => {
            const { data, error } = await database.newspaperRequests.table()
                .select('*')
                .eq('user_id', currentUser.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error fetching newspaper request:', error);
                return;
            }

            setNewspaperRequest(data);
            setIsLoading(false);
        };

        // Initial load
        loadNewspaperRequest();

        // Subscribe to changes
        const subscription = database.newspaperRequests.getChannel()
            .on('postgres_changes', 
                { 
                    event: '*', 
                    schema: 'public', 
                    table: 'newspaper_requests',
                    filter: `user_id=eq.${currentUser.id}`
                }, 
                (payload) => {
                    console.log('Newspaper request changed:', payload);
                    loadNewspaperRequest();
                }
            )
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [currentUser, setNewspaperRequest]);

    const updateNewspaperRequest = async (params: {
        title?: string | null;
        description?: string | null;
    } | null) => {
        if (!currentUser || !newspaperRequest) return false;

        setIsLoading(true);
        const { error } = await database.newspaperRequests.table()
            .update({
                title: params ? (params.title ?? newspaperRequest.title) : null,
                description: params ? (params.description ?? newspaperRequest.description) : null,
                is_customized: params !== null,
                updated_at: new Date().toISOString()
            })
            .eq('id', newspaperRequest.id)
            .eq('user_id', currentUser.id);

        setIsLoading(false);

        if (error) {
            console.error('Error updating newspaper request:', error);
            return false;
        }

        return true;
    };

    const requestNewspaperPost = useCallback(async () => {
        console.log('requestNewspaperPost', { currentUser, newspaperRequest });
        if (!currentUser) return false;

        setIsLoading(true);
        const { error } = await database.newspaperRequests.table()
            .update({
                status: 'requested',
                updated_at: new Date().toISOString()
            })
            //.eq('id', newspaperRequest.id)
            .eq('user_id', currentUser.id);

        setIsLoading(false);

        if (error) {
            console.error('Error updating newspaper request:', error);
            return false;
        }

        return true;
    }, [currentUser, newspaperRequest, newspaperRequest?.id, setIsLoading]);

    return {
        newspaperRequest,
        isLoading,
        updateNewspaperRequest,
        requestNewspaperPost
    };
}
